import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Quiz} from "types/Quiz";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {prefetchNearbyPages} from "services/query-client";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {QuestionMarkCircleIcon} from "@heroicons/react/outline";
import Dropdown from "components/Dropdown";
import {toTitleCase, truncate} from "services/string";
import DeleteQuiz from '../Delete';
import {useOperator} from "../../../context/OperatorContext";
import Link from "../../../components/Link";

function QuizList() {
    const {termFor} = useOperator();
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['quizzes.all', {page}], () => ngage.quizzes.all({page}), {keepPreviousData: true})

    const [deletingQuiz, setDeletingQuiz] = useState<Quiz | null>(null);

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('quizzes.all', page, ngage.quizzes.all)

    function getQuizzableLink(quiz: Quiz) {
        if (!quiz.quizzable || !quiz.quizzable_type) return '';

        switch(quiz.quizzable_type) {
            case 'exhibit':
                return `/exhibits/${quiz.quizzable.id}`
            case 'facility':
                return `/facilities/${quiz.quizzable.id}`
            case 'zone':
                return `/zones/${quiz.quizzable.id}`
            case 'trail-waypoint':
                return `/trails/${quiz.quizzable.trail.id}`
        }
    }

    function getQuizzableLinkText(quiz: Quiz) {
        if (!quiz.quizzable || !quiz.quizzable_type) return '';

        const quizzableType = toTitleCase(termFor(quiz.quizzable_type));

        switch(quiz.quizzable_type) {
            case 'trail-waypoint':
                return `${quiz.quizzable.name} - ${quiz.quizzable.trail.name} (${quizzableType})`
            default:
                return `${quiz.quizzable.name} (${quizzableType})`
        }
    }

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/quizzes/new')}>New</Button>

    return (
        <Page title={termFor('Quizzes')} actions={actions} breadcrumbs={{'App Content': '/', 'Interactive': '/', [termFor('Quizzes')]: "/quizzes"}}>
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Title</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader>Attached Entity</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Questions</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Attempts</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={7}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <QuestionMarkCircleIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No {termFor('Quizzes')}</span>
                                </div>
                            </TableData>
                        </TableRow>
                        : result?.data?.data.map((quiz, index) => (
                            <TableRow key={quiz.id} index={index} highlight>
                                <TableData>
                                    <div className="flex">
                                        <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                            {/* TODO: Convert to component */}
                                            <Dropdown position="left">
                                                {[
                                                    {name: 'View', icon: EyeIcon, onClick: () => navigate(`/quizzes/${quiz.id}`)},
                                                    {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/quizzes/${quiz.id}/edit`)},
                                                    {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingQuiz(quiz)}
                                                ]}
                                            </Dropdown>
                                        </div>

                                        <div className="flex group-hover:translate-x-1 transition-all">
                                            <QuestionMarkCircleIcon className="h-5 w-5 mr-2 text-gray-500" /> {quiz.title}
                                        </div>
                                    </div>
                                </TableData>
                                <TableData>{truncate(quiz.description, 25)}</TableData>
                                <TableData>
                                    {quiz.quizzable && quiz.quizzable_type?
                                        <Link
                                          to={getQuizzableLink(quiz)}>{getQuizzableLinkText(quiz)}
                                        </Link>
                                    : 'N/A'}
                                </TableData>
                                <TableData>{quiz.type?.name}</TableData>
                                <TableData alignment={TableData.alignments.RIGHT}>{quiz.question_count}</TableData>
                                <TableData alignment={TableData.alignments.RIGHT}>{quiz.maximum_attempts ?? 'Unlimited'}</TableData>
                                <TableData alignment={TableData.alignments.RIGHT}>
                                    {/* TODO: Convert to component */}
                                    <Dropdown>
                                        {[
                                            {name: 'View', icon: EyeIcon, onClick: () => navigate(`/quizzes/${quiz.id}`)},
                                            {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/quizzes/${quiz.id}/edit`)},
                                            {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingQuiz(quiz)}
                                        ]}
                                    </Dropdown>
                                </TableData>

                                {/* Delete confirmation modal */}
                                <DeleteQuiz
                                    quiz={deletingQuiz}
                                    show={quiz === deletingQuiz}
                                    onCancel={() => setDeletingQuiz(null)}
                                    onDelete={quiz => {
                                        navigate('/quizzes')
                                        notify("success", `${termFor('Quiz')} deleted`, `${quiz.title} has been permanently removed`)
                                    }}
                                    onDeleteFail={quiz => {
                                        setDeletingQuiz(null)
                                        notify("error", `${termFor('Quiz')} not deleted`, `${quiz.title} could not be removed`)
                                    }}
                                />
                            </TableRow>
                        ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default QuizList;